import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaLinkedinIn,
  FaArrowUp,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaWhatsapp,
} from "react-icons/fa";
import logo from "../../images/logo.jpg";

/* ─── Floating Particle Canvas ─── */
function ParticleCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = canvas.offsetWidth);
    let h = (canvas.height = canvas.offsetHeight);
    const particles = Array.from({ length: 55 }, () => ({
      x: Math.random() * w, y: Math.random() * h,
      r: Math.random() * 1.8 + 0.4,
      vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
      alpha: Math.random() * 0.5 + 0.1,
    }));
    let animId;
    function draw() {
      ctx.clearRect(0, 0, w, h);
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(96,175,245,${p.alpha})`;
        ctx.fill();
      });
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 110) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(96,175,245,${0.12 * (1 - dist / 110)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    }
    draw();
    const onResize = () => { w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight; };
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", onResize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }} />;
}

/* ─── Social Button ─── */
function SocialBtn({ href, icon: Icon, label, color }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
      className="ft-social-btn"
      onHoverStart={() => setHov(true)} onHoverEnd={() => setHov(false)}
      animate={{ y: hov ? -5 : 0, boxShadow: hov ? `0 8px 25px ${color}55` : "0 2px 8px rgba(0,0,0,0.2)", background: hov ? color : "rgba(255,255,255,0.08)", borderColor: hov ? color : "rgba(255,255,255,0.15)" }}
      transition={{ duration: 0.3 }}
    >
      <Icon />
    </motion.a>
  );
}

/* ─── Footer Link ─── */
function FooterLink({ to, children, delay = 0 }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay }}>
      <NavLink to={to} className="ft-nav-link"
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
        <motion.span className="ft-link-arrow" animate={{ x: hov ? 4 : 0, opacity: hov ? 1 : 0.4 }} transition={{ duration: 0.2 }}>›</motion.span>
        <span>{children}</span>
      </NavLink>
    </motion.div>
  );
}

/* ─── Contact Row ─── */
function ContactRow({ icon: Icon, children, delay }) {
  return (
    <motion.div className="ft-contact-row" initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay }}>
      <span className="ft-contact-icon"><Icon /></span>
      <span className="ft-contact-text">{children}</span>
    </motion.div>
  );
}

/* ─── Main Footer ─── */
function Footer() {
  const [showTop, setShowTop] = useState(false);
  const footerRef = useRef(null);
  const isInView = useInView(footerRef, { once: true, margin: "-60px" });

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.65, delay: d, ease: [0.16, 1, 0.3, 1] } })
  };

  return (
    <>
      {/* Scroll to Top */}
      <AnimatePresence>
        {showTop && (
          <motion.button onClick={scrollTop} className="ft-scroll-top" aria-label="Scroll to top"
            initial={{ opacity: 0, scale: 0.5, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 40 }}
            whileHover={{ scale: 1.12, y: -4 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <motion.span className="ft-scroll-pulse"
              animate={{ scale: [1, 1.6, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <FaArrowUp />
          </motion.button>
        )}
      </AnimatePresence>

      <footer ref={footerRef} className="ft-root">
        {/* ── Animated Wave Top ── */}
        <div className="ft-wave-top">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path className="ft-wave-path"
              d="M0,0 L1440,0 L1440,60 C1200,10 960,110 720,60 C480,10 240,110 0,60 Z"
            />
          </svg>
        </div>
        <ParticleCanvas />

        {/* Ambient Orbs */}
        <motion.div className="ft-orb ft-orb-1"
          animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div className="ft-orb ft-orb-2"
          animate={{ x: [0, -50, 0], y: [0, 50, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div className="ft-orb ft-orb-3"
          animate={{ x: [0, 40, -20, 0], y: [0, -30, 20, 0] }}
          transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* ── CTA Banner ── */}
        <motion.div className="ft-cta-section text-center"
          initial="hidden" animate={isInView ? "visible" : "hidden"} variants={fadeUp} custom={0}
        >
          <motion.h2 className="ft-cta-title" variants={fadeUp} custom={0.1}>
            Believe in possibilities and navigate<br className="d-none d-md-inline" />{" "}
            <span className="ft-cta-accent">beyond the expected</span> with us!
          </motion.h2>
          <motion.div variants={fadeUp} custom={0.25}>
            <NavLink to="/contact" className="ft-cta-btn">
              <span>Contact Us</span>
              <motion.span className="ft-cta-btn-shine"
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
              />
            </NavLink>
          </motion.div>
        </motion.div>

        {/* ── Divider ── */}
        <motion.div className="ft-divider"
          initial={{ scaleX: 0 }} animate={isInView ? { scaleX: 1 } : {}}
          transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        />

        {/* ── Main Grid ── */}
        <div className="ft-main">
          <div className="ft-container">
            <div className="ft-grid">

              {/* Brand Column */}
              <motion.div className="ft-col" initial="hidden" animate={isInView ? "visible" : "hidden"} variants={fadeUp} custom={0.15}>
                {/* Logo */}
                <motion.div className="ft-logo-wrap"
                  whileHover={{ scale: 1.04 }} transition={{ type: "spring", stiffness: 300 }}
                >
                  <img src={logo} alt="The Innovative Solutions" className="ft-logo" />
                </motion.div>
                <p className="ft-brand-tagline">An Innovative IT Software Company</p>
                <p className="ft-brand-desc">
                  Founded in 2020, The Innovative Solutions crafts bleeding-edge, highly optimized applications designed to launch companies to new heights in the digital landscape.
                </p>

                {/* Social */}
                <div className="ft-social-row">
                  <SocialBtn href="https://www.facebook.com/profile.php?id=100094476235504" icon={FaFacebookF} label="Facebook" color="#1877f2" />
                  <SocialBtn href="https://www.instagram.com/the.innovative.solutions/" icon={FaInstagram} label="Instagram" color="#e1306c" />
                  <SocialBtn href="https://twitter.com/theinnovatives3" icon={FaTwitter} label="Twitter" color="#1da1f2" />
                  <SocialBtn href="https://www.linkedin.com/in/the-innovative-solutions-18255222a/" icon={FaLinkedinIn} label="LinkedIn" color="#0a66c2" />
                  <SocialBtn href="https://wa.me/919371001138?text=Hello%21%20Team%20Innovative%20Solutions" icon={FaWhatsapp} label="WhatsApp" color="#25d366" />
                </div>
              </motion.div>

              {/* Explore Column */}
              <motion.div className="ft-col" initial="hidden" animate={isInView ? "visible" : "hidden"} variants={fadeUp} custom={0.25}>
                <h5 className="ft-col-title">
                  <span className="ft-col-title-line" />
                  Explore
                </h5>
                <div className="ft-links">
                  <FooterLink to="/" delay={0.1}>Home</FooterLink>
                  <FooterLink to="/about" delay={0.15}>About Us</FooterLink>
                  <FooterLink to="/services" delay={0.2}>Services</FooterLink>
                  <FooterLink to="/portfolio" delay={0.25}>Our Portfolio</FooterLink>
                  <FooterLink to="/gallery" delay={0.3}>Media Gallery</FooterLink>
                </div>
              </motion.div>

              {/* Resources Column */}
              <motion.div className="ft-col" initial="hidden" animate={isInView ? "visible" : "hidden"} variants={fadeUp} custom={0.32}>
                <h5 className="ft-col-title">
                  <span className="ft-col-title-line" />
                  Resources
                </h5>
                <div className="ft-links">
                  <FooterLink to="/career" delay={0.1}>Career Opportunities</FooterLink>
                  <FooterLink to="/privacy-policy" delay={0.15}>Privacy Guidelines</FooterLink>
                  <FooterLink to="/terms-and-conditions" delay={0.2}>Terms &amp; Conditions</FooterLink>
                  <FooterLink to="/contact" delay={0.25}>Contact Support</FooterLink>
                </div>

                {/* Contact mini-block */}
                <div className="ft-contact-block">
                  <ContactRow icon={FaMapMarkerAlt} delay={0.3}>Nashik, Maharashtra, India</ContactRow>
                  <ContactRow icon={FaPhoneAlt} delay={0.35}>+91 96650 16665</ContactRow>
                  <ContactRow icon={FaEnvelope} delay={0.4}>info@theinnovativesolutions.in</ContactRow>
                </div>
              </motion.div>

              {/* Find Us Map Column */}
              <motion.div className="ft-col" initial="hidden" animate={isInView ? "visible" : "hidden"} variants={fadeUp} custom={0.38}>
                <h5 className="ft-col-title">
                  <span className="ft-col-title-line" />
                  Find Us
                </h5>
                <div className="ft-map-wrap">
                  <iframe
                    src="https://maps.google.com/maps?width=435&amp;height=414&amp;hl=en&amp;q=the innovative solutions nashik&amp;t=&amp;z=15&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                    frameBorder="0"
                    scrolling="no"
                    title="Google Map - Footer Location"
                    className="ft-map-iframe"
                  ></iframe>
                </div>
              </motion.div>

            </div>
          </div>
        </div>

        {/* ── Bottom Bar ── */}
        <motion.div className="ft-bottom"
          initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div className="ft-container ft-bottom-inner">
            <p className="ft-copy">
              &copy; {new Date().getFullYear()}{" "}
              <span className="ft-copy-brand">The Innovative Solutions</span>. All rights reserved.
            </p>
            
            {/* Certifications Block */}
            <div className="ft-certifications-wrap">
              <span className="ft-cert-badge">ISO Certified</span>
              <span className="ft-cert-val ft-cert-iso">ISO 9001:2015</span>
              <span className="ft-cert-sep">|</span>
              <span className="ft-cert-badge">GST Certified</span>
              <span className="ft-cert-val ft-cert-gst">27AATFT4142K1ZM</span>
            </div>

            <div className="ft-bottom-links">
              <NavLink to="/privacy-policy" className="ft-bottom-link">Privacy Policy</NavLink>
              <span className="ft-bottom-sep">·</span>
              <NavLink to="/terms-and-conditions" className="ft-bottom-link">Terms &amp; Conditions</NavLink>
            </div>
          </div>
        </motion.div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,700;1,400;1,700&family=Manrope:wght@300;400;500;600;700;800&display=swap');

        /* ── Scroll to Top ── */
        .ft-scroll-top {
          position: fixed; bottom: 2rem; right: 2rem; z-index: 9999;
          width: 52px; height: 52px; border-radius: 50%; border: none;
          background: linear-gradient(135deg, #0ea5e9, #1e3a8a);
          color: #fff; font-size: 1.1rem; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 6px 28px rgba(14,165,233,0.45);
        }
        .ft-scroll-pulse {
          position: absolute; inset: -7px; border-radius: 50%;
          border: 2px solid rgba(14,165,233,0.45);
          pointer-events: none;
        }

        /* ── Root ── */
        .ft-root {
          font-family: 'Manrope', sans-serif;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 30%, #f1f5f9 60%, #e2e8f0 100%);
          position: relative; overflow: hidden; padding: 0 0 0; color: #475569;
        }

        /* ── Wave Top ── */
        .ft-wave-top {
          position: relative;
          z-index: 10;
          line-height: 0;
          margin-top: -2px; /* Align to the very top of the footer container */
        }
        .ft-wave-top svg {
          width: 100%; height: 90px; display: block;
        }
        .ft-wave-path {
          fill: #ffffff;
          transform-origin: center bottom;
          animation: ftSingleWave 10s ease-in-out infinite alternate;
        }
        @keyframes ftSingleWave {
          0%   { d: path("M0,0 L1440,0 L1440,60 C1200,10 960,110 720,60 C480,10 240,110 0,60 Z"); }
          100% { d: path("M0,0 L1440,0 L1440,60 C1140,110 900,10 660,60 C420,110 180,10 0,60 Z"); }
        }

        /* Orbs */
        .ft-orb { position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none; z-index: 1; }
        .ft-orb-1 { width: 480px; height: 480px; top: -120px; left: -80px; background: radial-gradient(circle, rgba(14,165,233,0.06) 0%, transparent 70%); }
        .ft-orb-2 { width: 400px; height: 400px; bottom: -100px; right: -80px; background: radial-gradient(circle, rgba(30,58,138,0.08) 0%, transparent 70%); }
        .ft-orb-3 { width: 280px; height: 280px; top: 40%; left: 45%; background: radial-gradient(circle, rgba(124,58,237,0.05) 0%, transparent 70%); }

        /* ── CTA Banner ── */
        .ft-cta-section {
          position: relative; z-index: 10;
          max-width: 820px; margin: 0 auto 3.5rem;
          padding: 0 1.5rem;
        }
        .ft-cta-title {
          font-family: 'Space Grotesk', sans-serif;
          font-size: clamp(1.8rem, 4.5vw, 2.8rem);
          font-weight: 700; color: #0f172a;
          line-height: 1.3; letter-spacing: -0.8px;
          margin-bottom: 2rem;
        }
        .ft-cta-accent {
          font-family: 'Playfair Display', serif;
          font-style: italic; font-weight: 700;
          background: linear-gradient(135deg, #0ea5e9 0%, #7c3aed 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .ft-cta-btn {
          display: inline-flex; align-items: center; position: relative; overflow: hidden;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1rem; font-weight: 600; color: #fff;
          text-decoration: none; padding: 0.85rem 2.5rem;
          background: linear-gradient(135deg, #0ea5e9, #7c3aed); border-radius: 50px;
          box-shadow: 0 6px 25px rgba(124,58,237,0.35);
          transition: all 0.35s cubic-bezier(0.16,1,0.3,1);
        }
        .ft-cta-btn:hover { transform: translateY(-4px); box-shadow: 0 12px 35px rgba(124,58,237,0.55); color: #fff; }
        .ft-cta-btn-shine {
          position: absolute; top: 0; left: 0;
          width: 40%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
          pointer-events: none;
        }

        /* Divider */
        .ft-divider {
          height: 1px; background: linear-gradient(90deg, transparent, rgba(0,0,0,0.06), transparent);
          transform-origin: left; position: relative; z-index: 2; margin: 0 2rem;
        }

        /* ── Main Grid ── */
        .ft-main { padding: 3.5rem 0 2rem; position: relative; z-index: 2; }
        .ft-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
        .ft-grid {
          display: grid; grid-template-columns: 2.2fr 1fr 1.2fr 1.8fr; gap: 3rem;
        }
        .ft-col {}
        
        /* Map Column in Footer */
        .ft-map-wrap {
          width: 100%;
          height: 180px;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          position: relative;
        }
        .ft-map-iframe {
          width: 100%;
          height: 100%;
          border: none;
          filter: grayscale(0.2) contrast(1.15) brightness(0.9);
        }

        /* Logo */
        .ft-logo-wrap {
          display: inline-block; margin-bottom: 1rem;
          border-radius: 10px; overflow: hidden;
          box-shadow: 0 6px 20px rgba(0,0,0,0.1);
        }
        .ft-logo { height: 56px; background: #fff; padding: 6px 14px; display: block; }
        .ft-brand-tagline {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.82rem; font-weight: 700;
          color: var(--contact-secondary); letter-spacing: 0.5px;
          margin-bottom: 1rem; text-transform: uppercase;
        }
        .ft-brand-desc {
          font-size: 0.92rem; color: #475569;
          line-height: 1.8; margin-bottom: 1.75rem; max-width: 420px;
        }

        /* Social */
        .ft-social-row { display: flex; gap: 0.85rem; }
        .ft-social-btn {
          width: 44px; height: 44px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: #475569; font-size: 1rem; text-decoration: none;
          border: 1px solid rgba(0, 0, 0, 0.1);
          cursor: pointer; transition: all 0.3s;
        }

        /* Column title */
        .ft-col-title {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1.05rem; font-weight: 700;
          color: #0f172a; margin-bottom: 1.75rem;
          display: flex; align-items: center; gap: 0.65rem;
          letter-spacing: 0.3px;
        }
        .ft-col-title-line {
          display: block; width: 22px; height: 2.5px;
          background: linear-gradient(90deg, #0ea5e9, #7c3aed);
          border-radius: 2px; flex-shrink: 0;
        }

        /* Links */
        .ft-links { display: flex; flex-direction: column; gap: 0.85rem; }
        .ft-nav-link {
          display: flex; align-items: center; gap: 0.5rem;
          color: #475569; text-decoration: none;
          font-size: 0.92rem; font-weight: 500;
          transition: color 0.3s ease;
        }
        .ft-nav-link:hover { color: #0f172a; }
        .ft-link-arrow {
          font-size: 1.1rem; font-weight: 700; color: #0ea5e9; line-height: 1;
        }

        /* Contact block */
        .ft-contact-block { margin-top: 2rem; display: flex; flex-direction: column; gap: 0.85rem; }
        .ft-contact-row { display: flex; align-items: flex-start; gap: 0.7rem; }
        .ft-contact-icon {
          width: 28px; height: 28px; flex-shrink: 0;
          background: rgba(14,165,233,0.1); border: 1px solid rgba(14,165,233,0.2);
          border-radius: 8px; display: flex; align-items: center; justify-content: center;
          color: var(--contact-secondary); font-size: 0.72rem;
        }
        .ft-contact-text { font-size: 0.85rem; color: #475569; line-height: 1.5; }

        /* Bottom bar */
        .ft-bottom {
          border-top: 1px solid rgba(0, 0, 0, 0.06);
          padding: 1.4rem 0; position: relative; z-index: 2;
        }
        .ft-bottom-inner {
          display: flex; justify-content: space-between;
          align-items: center; flex-wrap: wrap; gap: 0.75rem;
        }
        .ft-copy { font-size: 0.88rem; color: #64748b; margin: 0; }
        .ft-copy-brand { font-family: 'Space Grotesk', sans-serif; color: #0f172a; font-weight: 700; }
        .ft-bottom-links { display: flex; align-items: center; gap: 0.75rem; }
        .ft-bottom-link {
          color: #64748b; text-decoration: none;
          font-size: 0.85rem; transition: color 0.25s;
        }
        .ft-bottom-link:hover { color: #0f172a; }
        .ft-bottom-sep { color: rgba(0, 0, 0, 0.15); }
        
        .ft-certifications-wrap {
          display: inline-flex;
          align-items: center;
          gap: 0.65rem;
          background: rgba(15, 23, 42, 0.04);
          padding: 6px 14px;
          border-radius: 8px;
          box-shadow: none;
          border: 1px solid rgba(0, 0, 0, 0.05);
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.8rem;
          font-weight: 700;
        }
        .ft-cert-badge {
          background: rgba(15, 23, 42, 0.06);
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 2px 8px;
          border-radius: 4px;
          color: #334155;
          font-size: 0.72rem;
          letter-spacing: 0.2px;
        }
        .ft-cert-val {
          font-weight: 700;
        }
        .ft-cert-iso {
          color: #0284c7;
        }
        .ft-cert-gst {
          color: #b45309;
        }
        .ft-cert-sep {
          color: rgba(0, 0, 0, 0.15);
          margin: 0 0.15rem;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .ft-grid { grid-template-columns: 1fr; gap: 2.5rem; }
          .ft-brand-desc { max-width: 100%; }
          .ft-cta-title { font-size: 1.7rem; }
        }
      `}</style>
    </>
  );
}

export default Footer;
